import type { Segment3, Vec3 } from './viewTypes'

// ---------------------------------------------------------------------------
// Arestas de feição (feature edges) de uma malha triangulada.
//
// Emite-se uma aresta quando:
//  - só tem UM triângulo adjacente (fronteira / malha aberta), ou
//  - o ângulo diedro entre as duas faces adjacentes passa o limiar
//    (30° por omissão: apanha cantos de paredes, aros de vãos e degraus
//    e descarta a triangulação interna das faces planas), ou
//  - tem mais de dois triângulos adjacentes (malha não-manifold: no IFC
//    aparece bastante, mais vale desenhar de mais que de menos), ou
//  - é SILHUETA para a direcção de observação dada (ver abaixo).
//
// A soldadura de vértices é por posição QUANTIZADA (não por índice): a
// mesma face pode vir repartida por várias partes do item e os índices
// não se falam entre partes.
//
// Nota: o ângulo é calculado entre as NORMAIS das faces, portanto
// depende de as duas terem a mesma orientação de enrolamento. Faces
// coplanares bem enroladas dão 0°; um canto recto dá 90°. Malhas com
// enrolamento inconsistente produzem arestas a mais (aceitável: linha a
// mais no desenho, nunca linha a menos).
//
// SILHUETA (contorno aparente).
// O vinco é INDEPENDENTE da vista, e por isso sozinho não desenha o
// contorno de uma superfície curva: num cilindro tesselado a 32 lados as
// faces vizinhas fazem ~11°, muito abaixo do limiar, portanto NENHUMA
// aresta longitudinal passava e um tubo em planta saía sem as duas
// linhas paralelas afastadas do diâmetro (só os círculos dos topos). É o
// que se via nas plantas de MEP.
//
// O contorno aparente é o clássico teste de mudança de sentido: uma
// aresta partilhada por duas faces é silhueta quando uma das faces está
// virada para o observador e a outra não, ou seja quando
// `dot(n1, viewDir)` e `dot(n2, viewDir)` têm sinais diferentes.
//
// Tratamento do zero: uma face exactamente de perfil (dot = 0) conta
// como VIRADA PARA O OBSERVADOR (`d > -eps`), com uma folga `eps` para
// absorver o ruído das normais. Duas consequências, ambas desejadas:
//   - uma face plana vista de perfil (a face vertical de uma parede numa
//     planta) tem todas as suas normais em dot = 0 +- ruído, todas do
//     mesmo lado, portanto a triangulação interna NÃO vira contorno;
//   - numa superfície curva com uma faceta exactamente tangente sai UMA
//     linha de contorno (e não zero, como daria classificar o zero à
//     parte, nem duas, como daria contá-lo dos dois lados).
//
// O contorno é uma passagem só por vista (o viewDir é fixo), sem custo
// por fotograma.
// ---------------------------------------------------------------------------

export const FEATURE_ANGLE_DEG = 30

/** Grelha de quantização da soldadura, em metros. */
export const WELD_TOL = 1e-5

/** Folga do teste de sentido das faces. Absorve o ruído das normais
 *  calculadas a partir de posições em Float32: sem ela, duas faces
 *  coplanares vistas de perfil podiam sair com sinais opostos e a
 *  triangulação interna de uma parede aparecia toda na planta. */
export const SILHOUETTE_EPS = 1e-6

interface EdgeRec {
  a: Vec3
  b: Vec3
  n1: Vec3 | null
  n2: Vec3 | null
  count: number
}

/** Devolve as arestas de feição de uma malha em coordenadas de mundo.
 *
 *  `viewDir` é opcional: quando vem preenchido, junta-se às arestas de
 *  vinco o CONTORNO APARENTE nessa direcção (ver nota no topo). Passar
 *  `null`/`undefined` mantém o comportamento antigo, só vincos e
 *  fronteiras. */
export function featureEdges(
  positions: Float32Array | number[],
  indices: Uint32Array | Uint16Array | number[] | null,
  angleDeg: number = FEATURE_ANGLE_DEG,
  weldTol: number = WELD_TOL,
  viewDir?: Vec3 | null,
): Segment3[] {
  const out: Segment3[] = []
  if (!positions || positions.length < 9) return out
  const triCount = indices
    ? Math.floor(indices.length / 3)
    : Math.floor(positions.length / 9)
  if (triCount < 1) return out

  const inv = 1 / Math.max(weldTol, 1e-12)
  const vkey = (vi: number): string =>
    `${Math.round(positions[vi * 3] * inv)},${Math.round(
      positions[vi * 3 + 1] * inv,
    )},${Math.round(positions[vi * 3 + 2] * inv)}`

  const point = (vi: number): Vec3 => [
    positions[vi * 3],
    positions[vi * 3 + 1],
    positions[vi * 3 + 2],
  ]

  const edges = new Map<string, EdgeRec>()

  for (let t = 0; t < triCount; t++) {
    const i0 = indices ? indices[t * 3] : t * 3
    const i1 = indices ? indices[t * 3 + 1] : t * 3 + 1
    const i2 = indices ? indices[t * 3 + 2] : t * 3 + 2
    const p0 = point(i0)
    const p1 = point(i1)
    const p2 = point(i2)
    const n = faceNormal(p0, p1, p2)
    if (!n) continue // triângulo degenerado
    const k0 = vkey(i0)
    const k1 = vkey(i1)
    const k2 = vkey(i2)
    addEdge(edges, k0, k1, p0, p1, n)
    addEdge(edges, k1, k2, p1, p2, n)
    addEdge(edges, k2, k0, p2, p0, n)
  }

  const cosLimit = Math.cos((angleDeg * Math.PI) / 180)
  const vd = viewDir && Number.isFinite(viewDir[0]) ? viewDir : null
  /** true = face virada para o observador. O zero conta como virada
   *  (ver nota da silhueta no topo). */
  const facing = (n: Vec3): boolean =>
    n[0] * (vd as Vec3)[0] + n[1] * (vd as Vec3)[1] + n[2] * (vd as Vec3)[2] >
    -SILHOUETTE_EPS
  for (const rec of edges.values()) {
    if (rec.count === 1 || rec.count > 2) {
      out.push({ a: rec.a, b: rec.b })
      continue
    }
    const n1 = rec.n1
    const n2 = rec.n2
    if (!n1 || !n2) {
      out.push({ a: rec.a, b: rec.b })
      continue
    }
    const c = n1[0] * n2[0] + n1[1] * n2[1] + n1[2] * n2[2]
    // cos decrescente com o ângulo: c < cos(limiar) significa
    // ângulo > limiar.
    if (c < cosLimit) {
      out.push({ a: rec.a, b: rec.b })
      continue
    }
    if (vd && facing(n1) !== facing(n2)) out.push({ a: rec.a, b: rec.b })
  }

  return out
}

function addEdge(
  edges: Map<string, EdgeRec>,
  ka: string,
  kb: string,
  pa: Vec3,
  pb: Vec3,
  n: Vec3,
): void {
  if (ka === kb) return
  const key = ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`
  const rec = edges.get(key)
  if (!rec) {
    edges.set(key, { a: pa, b: pb, n1: n, n2: null, count: 1 })
    return
  }
  rec.count++
  if (!rec.n2) rec.n2 = n
}

/** Normal unitária da face, ou null se o triângulo for degenerado. */
function faceNormal(p0: Vec3, p1: Vec3, p2: Vec3): Vec3 | null {
  const ax = p1[0] - p0[0]
  const ay = p1[1] - p0[1]
  const az = p1[2] - p0[2]
  const bx = p2[0] - p0[0]
  const by = p2[1] - p0[1]
  const bz = p2[2] - p0[2]
  const nx = ay * bz - az * by
  const ny = az * bx - ax * bz
  const nz = ax * by - ay * bx
  const len = Math.hypot(nx, ny, nz)
  if (!Number.isFinite(len) || len < 1e-14) return null
  return [nx / len, ny / len, nz / len]
}
